import { formatMoney } from "../helpers/money";
import axios from "axios";
import { store } from "../store";
import config from "../config";
import authHeader from "./auth-header";
import { encodeQueryValue } from "./queryString";
import moment from "moment";
import { subscriptionCatalogRequest } from "./subscriptionCatalogRequest";

const API_URL = config.API_URL;

const positiveInteger = (value) => {
  if (value === null || value === undefined || value === "" || typeof value === "boolean") return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const normalizeMoney = (value) => {
  if (value && typeof value === "object") {
    if (value.amount != null) return normalizeMoney(value.amount);
    if (value.amount_minor != null && Number.isFinite(Number(value.amount_minor))) {
      return Number(value.amount_minor) / 100;
    }
  }
  if (value === null || value === undefined || value === "") return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
};

const normalizeCurrency = (value, fallback = "AUD") => {
  const candidate = value && typeof value === "object" ? value.currency : value;
  return typeof candidate === "string" && candidate.trim()
    ? candidate.trim().toUpperCase()
    : fallback;
};

export const normalizeSubscriptionDetail = (detail = {}) => {
  const normalized = { ...detail };
  const detailCurrency = normalized.currency
    || normalized.amount?.currency
    || normalized.one_off_amount?.currency;

  normalized.currency = normalizeCurrency(detailCurrency);

  if (normalized.amount !== undefined) normalized.amount = normalizeMoney(normalized.amount);
  if (normalized.one_off_amount !== undefined) {
    normalized.one_off_amount = normalized.one_off_amount == null
      ? ""
      : normalizeMoney(normalized.one_off_amount);
  }
  if (normalized.expiry_months === null || normalized.expiry_months === undefined || normalized.expiry_months === "") {
    normalized.expiry_months = "never_expire";
  } else if (normalized.expiry_months !== "never_expire") {
    normalized.expiry_months = String(normalized.expiry_months);
  }

  return normalized;
};

export const normalizeSubscriptionServices = (detail = {}) => {
  if (Array.isArray(detail.subservice)) return detail.subservice;
  if (Array.isArray(detail.services)) return detail.services;
  return [];
};

export const normalizeSubscriptionProducts = (detail = {}) => (
  Array.isArray(detail.products) ? detail.products : []
);

export const validateSubscriptionEntitlements = (values = {}) => {
  if (values.benefit_type === "wallet_credit") return {};
  const services = Array.isArray(values.subservice) ? values.subservice : [];
  const products = Array.isArray(values.products) ? values.products : [];
  if (services.length === 0 && products.length === 0) {
    return { entitlements: "Add at least one service or product." };
  }
  const errors = {};
  [...services.map((item, index) => ["subservice", item, index]), ...products.map((item, index) => ["products", item, index])]
    .forEach(([field, item, index]) => {
      const quantity = Number(item?.qty ?? item?.quantity);
      if (!Number.isInteger(quantity) || quantity <= 0) {
        errors[`${field}[${index}].qty`] = "Quantity must be a positive number.";
      }
    });
  return errors;
};

// SOF-62 inclusions: an optional gift voucher amount and an optional
// percentage discount applied to future product purchases. Both are optional,
// so an untouched field is a valid value that must not block submission.
const inclusionMoneyInputValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const normalized = normalizeMoney(value);
  if (normalized === null || normalized === undefined || normalized === "") return "";
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : String(normalized);
};

export const buildSubscriptionInclusionFormValues = (detail = {}) => ({
  voucher_amount: inclusionMoneyInputValue(detail?.voucher_amount),
  product_discount_percentage: detail?.product_discount_percentage == null
    ? ""
    : String(detail.product_discount_percentage),
});

const isBlank = (value) => value === null || value === undefined || String(value).trim() === "";
const isPositiveMoneyInput = (value) => /^\d+(\.\d{1,2})?$/.test(String(value).trim())
  && Number(value) > 0;
const isDiscountPercentageInput = (value) => /^\d+(\.\d{1,2})?$/.test(String(value).trim())
  && Number(value) > 0
  && Number(value) <= 100;

export const validateSubscriptionInclusions = (values = {}) => {
  const errors = {};
  if (!isBlank(values.voucher_amount) && !isPositiveMoneyInput(values.voucher_amount)) {
    errors.voucher_amount = "Voucher amount must be a positive amount with at most two decimals.";
  }
  if (
    !isBlank(values.product_discount_percentage)
    && !isDiscountPercentageInput(values.product_discount_percentage)
  ) {
    errors.product_discount_percentage =
      "Product discount must be above 0 and at most 100 with at most two decimals.";
  }
  return errors;
};

// A blank field means "no inclusion", which the API clears with an explicit
// null. An unusable value is passed through untouched so the API rejects it
// with a field error instead of silently dropping the inclusion.
const inclusionMutationValue = (value, predicate) => {
  if (isBlank(value)) return null;
  return predicate(value) ? normalizeMoney(value) : String(value).trim();
};

const inclusionPercentageMutationValue = (value) => {
  if (isBlank(value)) return null;
  return isDiscountPercentageInput(value) ? Number(value) : String(value).trim();
};

const servicePriceFromDefaultPrice = (defaultPrice) => {
  if (!defaultPrice || typeof defaultPrice !== "object") return undefined;
  const minor = Number(defaultPrice.amount_minor);
  if (!Number.isFinite(minor)) return undefined;
  return { price: minor / 100 };
};

export const formatIncludedServicePrice = (value, currency) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "number" && !Number.isFinite(value)) return "";
  return formatMoney(value, currency);
};

export const formatWalletMoney = (value, currency) => {
  const parsed = parseFloat(value);
  if (!Number.isFinite(parsed)) return formatMoney(0, currency);
  return formatMoney(parsed, currency);
};

export const walletContributionSummary = (values = {}) => {
  const amount = parseFloat(values.amount);
  const payments = parseInt(values.no_of_payments, 10);
  const hasAmount = Number.isFinite(amount) && amount > 0;
  const hasPayments = Number.isFinite(payments) && payments > 0;
  const customFirstPayment = values.one_off_payments == 1;
  const firstPaymentAmount = customFirstPayment && values.one_off_amount !== "" && values.one_off_amount != null
    ? parseFloat(values.one_off_amount)
    : undefined;
  const totalCredited = hasAmount && hasPayments
    ? (Number.isFinite(firstPaymentAmount)
      ? firstPaymentAmount + amount * (payments - 1)
      : amount * payments)
    : 0;
  return {
    amount: hasAmount ? amount : 0,
    perPayment: hasAmount ? amount : 0,
    totalPayments: hasPayments ? payments : 0,
    frequency: values.repeat_time_option || "",
    currency: values.currency || "AUD",
    hasAmount,
    hasPayments,
    totalCredited,
  };
};

export const buildSubscriptionServiceFormRows = (source = [], currentRows = []) => {
  const relations = Array.isArray(source) ? source : normalizeSubscriptionServices(source);
  const currentById = new Map(
    (Array.isArray(currentRows) ? currentRows : []).map((row) => [String(row?.id), row]),
  );

  return relations.map((relation) => {
    const service = relation?.services || relation?.service || relation || {};
    const id = service?.id ?? service?.service_id ?? relation?.service_id;
    const current = currentById.get(String(id));
    return {
      ...service,
      id,
      name: service?.name ?? service?.service_name ?? relation?.name,
      defaultserviceprice: service?.defaultserviceprice
        ?? relation?.defaultserviceprice
        ?? servicePriceFromDefaultPrice(relation?.default_price),
      qty: current && Object.prototype.hasOwnProperty.call(current, "qty")
        ? current.qty
        : (relation?.qty ?? relation?.quantity ?? 1),
    };
  });
};

export const buildSubscriptionProductFormRows = (source = []) => {
  const products = Array.isArray(source)
    ? source
    : (Array.isArray(source?.products) ? source.products : []);

  return products
    .map((relation) => {
      const product = relation?.product || relation?.products || relation || {};
      const id = product?.id ?? product?.product_id ?? relation?.product_id ?? relation?.id;
      const qty = relation?.qty ?? relation?.quantity;
      return { id, qty: qty === undefined || qty === null ? 1 : qty };
    })
    .filter((row) => positiveInteger(row.id));
};

export const validateSubscriptionServiceRows = (rows = []) => {
  const rowErrors = (Array.isArray(rows) ? rows : []).map(() => ({}));
  const seen = new Set();
  let invalid = false;
  let duplicate = false;

  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    const id = positiveInteger(row?.id ?? row?.service_id ?? row?.services?.id);
    const qty = positiveInteger(row?.qty ?? row?.quantity);
    if (!qty || qty > 1000) {
      rowErrors[index].qty = "Quantity must be a whole number between 1 and 1000.";
      invalid = true;
    }
    if (id && seen.has(id)) {
      rowErrors[index].id = "This service has already been included.";
      duplicate = true;
    }
    if (id) seen.add(id);
  });

  if (!invalid && !duplicate) return { errors: {}, message: "" };
  return {
    errors: { subservice: rowErrors },
    message: invalid && duplicate
      ? "Fix the highlighted service quantities and remove duplicate services."
      : invalid
        ? "Fix the highlighted service quantities."
        : "Remove duplicate services.",
  };
};

export const validateSubscriptionProductRows = (rows = []) => {
  const rowErrors = (Array.isArray(rows) ? rows : []).map(() => ({}));
  const seen = new Set();
  let invalid = false;
  let duplicate = false;

  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    const id = positiveInteger(row?.id ?? row?.product_id ?? row?.product?.id);
    const qty = positiveInteger(row?.qty ?? row?.quantity);
    if (!qty || qty > 1000) {
      rowErrors[index].qty = "Quantity must be a whole number between 1 and 1000.";
      invalid = true;
    }
    if (id && seen.has(id)) {
      rowErrors[index].id = "This product has already been included.";
      duplicate = true;
    }
    if (id) seen.add(id);
  });

  if (!invalid && !duplicate) return { errors: {}, message: "" };
  return {
    errors: { products: rowErrors },
    message: invalid && duplicate
      ? "Fix the highlighted product quantities and remove duplicate products."
      : invalid
        ? "Fix the highlighted product quantities."
        : "Remove duplicate products.",
  };
};

export const prepareSubscriptionMutationValues = (values = {}) => {
  const prepared = { ...values };
  const payments = parseFloat(prepared.no_of_payments);
  if (!Number.isNaN(payments) && (payments === 0 || payments === 1)) {
    prepared.one_off_payments = 0;
  }
  return prepared;
};

const normalizeBoolean = (value) => value === true || value === 1 || value === "1" || value === "true";

const normalizeRepeatOption = (value) => ({
  Weekly: "Weekly",
  Fortnightly: "Fortnightly",
  Monthly: "Monthly",
  Yearly: "Yearly",
  // Keep older clients working while sending Laravel's stored enum values.
  Week: "Weekly",
  Fortnight: "Fortnightly",
  Month: "Monthly",
  Year: "Yearly",
}[value] || value);

const normalizeCommencementDate = (value) => {
  if (typeof value !== "string" || !value.includes("/")) return value;
  const parsed = moment(value.replace(/\//g, "-"), "DD-MM-YYYY");
  return parsed.isValid() ? `${parsed.format("YYYY-MM-DD")} ${moment().format("HH:mm:ss")}` : value;
};

export const normalizeSubscriptionPayload = (values = {}) => {
  const payload = { ...values };
  delete payload.id;
  delete payload.auth_key;
  delete payload.action;
  delete payload.idempotency_key;

  payload.currency = normalizeCurrency(
    payload.currency
      ?? payload.amount?.currency
      ?? payload.one_off_amount?.currency,
  );

  if (payload.amount !== undefined) payload.amount = normalizeMoney(payload.amount);
  if (payload.one_off_amount !== undefined && payload.one_off_amount !== "") {
    payload.one_off_amount = normalizeMoney(payload.one_off_amount);
  } else {
    delete payload.one_off_amount;
  }
  if (payload.repeat_time !== undefined) {
    const repeatTime = positiveInteger(payload.repeat_time);
    if (repeatTime) payload.repeat_time = repeatTime;
    else delete payload.repeat_time;
  }
  if (payload.repeat_time_option !== undefined) payload.repeat_time_option = normalizeRepeatOption(payload.repeat_time_option);
  if (payload.benefit_type === "wallet_credit") {
    payload.subservice = [];
    payload.products = [];
    payload.discount_code_ids = [];
    payload.repeat_time_option = ["Weekly", "Monthly"].includes(payload.repeat_time_option)
      ? payload.repeat_time_option
      : "Monthly";
  } else {
    payload.benefit_type = payload.benefit_type || "entitlements";
  }
  if (payload.no_of_payments !== undefined) {
    const noOfPayments = positiveInteger(payload.no_of_payments);
    if (noOfPayments) payload.no_of_payments = noOfPayments;
    else delete payload.no_of_payments;
  }
  if (payload.expiry_months !== undefined && payload.expiry_months !== "never_expire") {
    const expiryMonths = positiveInteger(payload.expiry_months);
    if (expiryMonths) payload.expiry_months = expiryMonths;
  }
  if (payload.commencement_date !== undefined) payload.commencement_date = normalizeCommencementDate(payload.commencement_date);

  ["one_off_payments", "automatic_renew", "is_subscription_purchase_online", "is_subscription_hide_online"].forEach((key) => {
    payload[key] = normalizeBoolean(payload[key]);
  });

  if (Array.isArray(payload.subservice)) {
    payload.subservice = payload.subservice
      .map((service) => {
        const serviceId = positiveInteger(service?.id ?? service?.service_id ?? service?.services?.id);
        const rawQty = service?.qty ?? service?.quantity;
        const qty = positiveInteger(rawQty);
        return serviceId ? { id: serviceId, qty: qty ?? rawQty } : null;
      })
      .filter(Boolean);
  }

  if (Array.isArray(payload.products)) {
    payload.products = payload.products
      .map((product) => {
        const productId = positiveInteger(product?.id ?? product?.product_id ?? product?.product?.id);
        const qty = positiveInteger(product?.qty ?? product?.quantity) || 1;
        return productId ? { id: productId, qty } : null;
      })
      .filter(Boolean);
  }

  if (Array.isArray(payload.discount_code_ids)) {
    payload.discount_code_ids = [...new Set(payload.discount_code_ids.map(positiveInteger).filter(Boolean))];
  }

  if ("voucher_amount" in payload) {
    payload.voucher_amount = inclusionMutationValue(payload.voucher_amount, isPositiveMoneyInput);
  }
  if ("product_discount_percentage" in payload) {
    payload.product_discount_percentage = inclusionPercentageMutationValue(
      payload.product_discount_percentage,
    );
  }

  return payload;
};

const idempotencyKey = (values, operation) => {
  if (values && values.idempotency_key) return values.idempotency_key;
  const id = positiveInteger(values && values.id) || "new";
  return `subscription-${operation}-${id}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const requestOptions = (values, operation) => ({
  headers: {
    ...authHeader(),
    "Idempotency-Key": idempotencyKey(values, operation),
  },
});

const create = (values = {}) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const action = "afterlogin/subscription/store";
  const payload = normalizeSubscriptionPayload(values);
  payload.auth_key = auth_key;
  payload.action = action;
  return axios.post(API_URL + action, payload, requestOptions(values, "create"));
};

const update = (values = {}) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const id = positiveInteger(values.id) || values.id;
  const action = "afterlogin/subscription/update/" + id;
  const payload = normalizeSubscriptionPayload(values);
  payload.auth_key = auth_key;
  payload.action = action;
  return axios.post(API_URL + action, payload, requestOptions(values, "update"));
};

const view = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const sort = values && values.sort;
  const page = positiveInteger(values && values.page);
  const next_page_url = values && values.next_page_url;
  const result = values && values.result ? values.result : "";
  let sortstring = "";
  if (sort) {
    let sortArray = [];
    Object.keys(sort).map(function (key, index) {
      return (sortArray[index] = `sort[${key}]=${sort[key]}`);
    });
    if (sortArray.length > 0) {
      let jsort = sortArray.join("&");
      sortstring = jsort;
    }
  }
  let pagination = true;
  if(values){
    if (positiveInteger(values.id)) {
      pagination = false;
    }
    if(values.option){
      pagination = false;
    }
    if (typeof values.pagination === "boolean") {
      pagination = values.pagination;
    }
  }
  const action = page ? `afterlogin/subscription/view?page=${page}&${sortstring}` : `afterlogin/subscription/view?${sortstring}`;
  const data = {
    auth_key: auth_key,
    action: action,
    salon_id: auth.user.salon_id,
    pagination: pagination, //true or false
    result: result, //business_name,owner_name
    option: values && values.option ? values.option : "",
    page: page || 1,
    limit: positiveInteger(values && values.limit) || 100,
    q: typeof values?.q === "string" ? values.q : "",
  };
  const id = positiveInteger(values && values.id);
  if (id) data.id = id;
  return axios.post(next_page_url ? `${next_page_url}&${sortstring}` : API_URL + action, data, { headers: authHeader() });
};

const deleted = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const action = `afterlogin/subscription/delete/${values.id}`;
  const data = {
    auth_key: auth_key,
    action: action,
  };
  return axios.post(API_URL + action, data, { ...requestOptions(values, "delete"), ...(values.archive === true ? { deleteConfirmation: false } : {}) });
};

const suggetionlist = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const page = positiveInteger(values && values.page);
  const next_page_url = values && values.next_page_url;
  let q = values && values.q ? values.q : "";
  const encodedQ = encodeQueryValue(q);
  const action = page ? `afterlogin/subscription/view?page=${page}&q=${encodedQ}` : `afterlogin/subscription/view?q=${encodedQ}`;
  const data = {
    auth_key: auth_key,
    action: action,
    pagination: true, //true or false
    apicall: "suggetion",
    q,
  };
  const id = positiveInteger(values && values.id);
  if (id) data.id = id;
  return axios.post(next_page_url ? `${next_page_url}&q=${encodedQ}` : API_URL + action, data, { headers: authHeader() });
};

const invoice = (values) => {
  const auth = store.getState().auth;
  const auth_key = auth.user.auth_key;
  const action = `afterlogin/subscription/invoice`;
  const data = {
    auth_key: auth_key,
    action: action,
    pagination: false,
  };
  const subscriptionId = positiveInteger(values && values.subscription_id);
  const clientSubscriptionPlanId = positiveInteger(values && values.client_subscription_plan_id);
  if (subscriptionId) data.subscription_id = subscriptionId;
  else if (clientSubscriptionPlanId) data.client_subscription_plan_id = clientSubscriptionPlanId;
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const services = (values) => {
  const auth_key = store.getState().auth.user.auth_key;
  const request = subscriptionCatalogRequest(values);
  const action = "afterlogin/subscription/services";
  const data = {
    pagination: true,
    auth_key,
    action,
    staff: values?.staff || "",
    gprice: values?.gprice || "",
    ...request,
  };
  const service_id = positiveInteger(values?.service_id);
  if (service_id) data.service_id = service_id;
  return axios.post(API_URL + action, data, { headers: authHeader() });
};

const subscriptionApiController = {
  create,
  update,
  view,
  deleted,
  suggetionlist,
  services,
  invoice,
};
export default subscriptionApiController;
